<template>
  <aside class="SideFilter">
    <SideFilterDropdown class="mb-12" title="Price">
      <PricesMultiRangeSlider
        :min="0"
        :gap="100"
        :max="6000"
        :step="100"
        @select="select"
        ref="pricesMultiRangeSlider"
        desktop-variant
      />

      <MultiSelect
        :class="{ 'opacity-50': !isPriceOptionsActive }"
        class="mt-2"
        :collections="listingFilters.prices"
        :selected="prices"
        type="prices"
        @select="select"
      />

      <div class="ml-2">
        <div class="border-b mt-3 mb-5" />

        <div class="flex items-center" v-if="currentMarket.isCore">
          <button
            type="button"
            :class="framing_filter_preference ? 'bg-bt-blue-500' : 'bg-gray-200'"
            class="relative inline-flex h-10 w-17 shrink-0 items-center cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-hidden"
            role="switch"
            @click="toggleFramingPreference"
          >
            <span
              aria-hidden="true"
              :class="framing_filter_preference ? 'translate-x-8' : 'translate-x-1'"
              class="pointer-events-none inline-block h-8 w-8 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            ></span>
          </button>
          <span class="ml-6 font-normal">Show Price Including Frame</span>
        </div>
      </div>
    </SideFilterDropdown>

    <SideFilterDropdown class="my-12" title="Medium" id="filters-medium">
      <MultiSelect
        v-if="category !== 'All Art'"
        :collections="mediumsBySelected"
        :selected="mediums"
        :url="computedUrl"
        :showMax="5"
        type="mediums"
        @select="select"
      />
      <MultiSelect
        v-else
        :collections="config.mediums"
        :selected="mediums"
        :url="computedUrl"
        :showMax="showMax"
        type="mediums"
        @select="select"
      />
    </SideFilterDropdown>

    <SideFilterDropdown class="my-12" title="Size">
      <MultiSelect :collections="listingFilters.sizes" type="sizes" :selected="sizes" @select="selectSize" />
      <div class="text-center">OR</div>
      <DimensionInput />
    </SideFilterDropdown>

    <SideFilterDropdown class="my-12" title="Orientation">
      <OrientationSelect :selected="orientations" @select="select" />
    </SideFilterDropdown>

    <SideFilterDropdown class="my-12" title="Style" id="filters-style">
      <MultiSelect
        :collections="config.art_styles"
        type="art_styles"
        :selected="art_styles"
        :url="computedUrl"
        :showMax="showMax"
        @select="select"
      />
    </SideFilterDropdown>

    <SideFilterDropdown class="my-12" title="Subject">
      <MultiSelect
        :collections="allCollections"
        type="collections"
        :selected="collections"
        :url="computedUrl"
        :showMax="showMax"
        @select="select"
      />
    </SideFilterDropdown>

    <SideFilterDropdown class="my-12" title="Colour">
      <ColorFilter :options="config.colors" />
    </SideFilterDropdown>

    <SideFilterDropdown class="my-12" title="Artist location" v-if="currentMarket.isCore">
      <MultiSelect :collections="listingFilters.locations" type="locations" :selected="locations" @select="select" />
    </SideFilterDropdown>

    <SideFilterDropdown class="my-12" title="Display">
      <div class="mx-2">
        <BigCheckbox label="Ready to Hang" @change="toggleHang" :active="readyToHang" />
        <BigCheckbox
          v-if="userConfig.signedIn"
          label="Artists I Follow"
          @change="toggleFollows"
          :active="fromFollows"
        />
        <a
          v-else
          href="#"
          class="BigCheckbox flex items-center"
          data-toggle="modal"
          data-target="#session-modal"
          title="Artists I Follow"
        >
          <span class="BigCheckbox_radio fa-layers fa-2x">
            <FaIcon :icon="['fal', 'square']" class="unchecked text-grey-light" />
          </span>
          <span
            class="ml-2 text-grey-light hover:text-grey-light hover:no-underline focus:text-grey-light focus:no-underline cursor-default"
          >
            Artists I Follow
          </span>
        </a>
        <BigCheckbox label="Exclude Sold" @change="toggleSold" :active="availability == 'for_sale_web'" />
      </div>
    </SideFilterDropdown>

    <SideFilterDropdown :initial-open="false" class="my-12" title="Sort By">
      <RadioSelect :collections="listingFilters.sortOptions()" type="sort" :selected="sort" @select="select" />
    </SideFilterDropdown>
  </aside>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"

import PricesMultiRangeSlider from "@/pages/ListingsPage/filters/PricesMultiRangeSlider.vue"
import ListingFilters from "@/lib/ListingFilters"
import BigCheckbox from "@/components/BigCheckbox.vue"
import SideFilterDropdown from "./side/SideFilterDropdown.vue"
import MultiSelect from "./side/MultiSelect.vue"
import RadioSelect from "./side/RadioSelect.vue"
import ColorFilter from "./ColorFilter.vue"
import RangeInput from "./side/RangeInput.vue"
import DimensionInput from "./side/DimensionInput.vue"
import OrientationSelect from "./side/OrientationSelect.vue"

import { actions as searchStoreAction } from "@/components/SearchBar/store.js"

import filterToPath from "@/lib/Listing/FilterToPath"

export default {
  name: "ListingsPage",
  data() {
    return {
      searchTerm: "",
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.searchTerm = this.term
    })
  },
  methods: {
    computedUrl(type, selected, slug) {
      var new_type_filter = {}
      if (selected.includes(slug)) {
        new_type_filter[type] = selected.filter((item) => item !== slug)
      } else {
        new_type_filter[type] = selected.concat(slug)
      }
      return filterToPath(Object.assign(Object.assign({}, this.filters), new_type_filter))
    },
    select(filter) {
      this.mergeFilter(filter)
    },
    reset() {
      Object.values(this.$refs).forEach((v) => v?.reset?.())
      this.searchTerm = ""
      this.resetFilters()
      searchStoreAction.reset()
    },
    selectSize(filter) {
      this.mergeFilter({ ...filter, height: [], width: [] })
      this.resetDimension()
    },
    search() {
      this.mergeFilter({ term: this.searchTerm })
    },
    clearSearch() {
      this.searchTerm = ""
    },
    deleteFilter(type) {
      this.deleteFilters([type])
    },
    toggleSold(includeSold) {
      this.mergeFilter({ availability: includeSold ? "for_sale_web" : "all" })
    },
    toggleFollows(follows) {
      this.mergeFilter({ from_followeds: follows ? "from_followeds" : "all" })
    },
    toggleHang(hang) {
      this.mergeFilter({ ready_to_hang: hang ? "ready_to_hang" : "all" })
    },
    toggleFramingPreference() {
      this.mergeFilter({ framing_filter_preference: !this.framing_filter_preference })
    },
    ...mapActions("filter", ["mergeFilter", "resetFilters", "deleteFilters", "resetDimension"]),
  },
  computed: {
    showClear() {
      if (this.searchTerm != "") {
        return true
      } else {
        return false
      }
    },
    listingFilters() {
      return ListingFilters
    },
    mediumsBySelected() {
      return [...(this.config.mediums || [])].sort((m) => (this.mediums.includes(m.value) ? -1 : 1))
    },
    isPriceOptionsActive() {
      return this.price_range.length === 0
    },
    ...mapState("filter", {
      filters: ({ filters }) => filters,
      category: ({ filters }) => filters.category,
      allCollections: ({ config }) => config.collections,
      config: ({ config }) => config,
      collections: ({ filters }) => filters.collections,
      art_styles: ({ filters }) => filters.art_styles,
      sizes: ({ filters }) => filters.sizes,
      locations: ({ filters }) => filters.locations,
      orientations: ({ filters }) => filters.orientations,
      artwork_statuses: ({ filters }) => filters.artwork_statuses,
      sort: ({ filters }) => filters.sort,
      colors: ({ filters }) => filters.colors,
      mediums: ({ filters }) => filters.mediums,
      prices: ({ filters }) => filters.prices,
      availability: ({ filters }) => filters.availability,
      term: ({ filters }) => filters.term,
      width_range: ({ filters }) => filters.width_range,
      height_range: ({ filters }) => filters.height_range,
      price_range: ({ filters }) => filters.price_range,
      framing_filter_preference: ({ filters }) => filters.framing_filter_preference,
    }),
    showMax() {
      switch (window.location.hash) {
        case "#filters-style":
          return 777
        case "#filters-medium":
          return 777
        default:
          return 5
      }
    },
    ...mapGetters("filter", ["fromFollows", "readyToHang"]),
  },
  watch: {
    term(term, _oldTerm) {
      this.searchTerm = term
    },
    prices(newVal, oldVal) {
      // It handle the prices filter behaviour on Mobile and Desktop
      const fullRange = this.listingFilters.prices[0].value
      const hasFullRangeNewVal = newVal.includes(fullRange)
      const hasFullRangeOldVal = oldVal.includes(fullRange)

      // Remove a full range from the prices filter if the new prices filter applied
      if (hasFullRangeNewVal && hasFullRangeOldVal && newVal.length > 1) {
        const prices = newVal.filter((el) => el !== fullRange)
        if (newVal.length !== prices.length) {
          return this.mergeFilter({
            prices,
          })
        }
      }

      // Remove other prices filters if the full range filter applied
      if (!hasFullRangeOldVal && hasFullRangeNewVal) {
        return this.mergeFilter({
          prices: [fullRange],
        })
      }
    },
  },
  components: {
    PricesMultiRangeSlider,
    SideFilterDropdown,
    MultiSelect,
    RadioSelect,
    ColorFilter,
    BigCheckbox,
    RangeInput,
    DimensionInput,
    OrientationSelect,
  },
}
</script>
